import * as React from 'react';
import { Link } from 'react-router-dom';
import { Award, ExternalLink, FileText, Languages, Loader2, MapPin, Tag } from 'lucide-react';
import MarkdownIt from 'markdown-it';

import { Badge } from '@/components/ui/badge';
import { publicApiBaseUrl } from '@/data/public-advisors';

const markdown = MarkdownIt('commonmark', { html: false, linkify: true });

type PublicProfile = {
  id: string;
  display_name: string;
  headline: string;
  bio: string;
  photo_url: string;
  industries: string;
  certifications: string[];
  years_experience: number | null;
  specializations: string;
  website_url?: string;
  linkedin_url?: string;
  stats?: { clients: number; running_contracts: number; published_articles: number };
};

export type PublicOfferSummary = {
  id: string;
  title: string;
  introduction: string;
  currency: string;
  billing_mode: 'direct' | 'via_aoc';
  packages: Array<{ id?: string; name: string; summary: string; monthly_fee: string; services: Array<{ id?: string; text: string }>; hourly_rates: Array<{ id?: string; label: string; hourly_rate: string }> }>;
};

type PublicProfilePayload = {
  profile: PublicProfile;
  blog_posts: Array<{ id: string; title: string; slug: string; excerpt?: string; published_at?: string | null }>;
  public_offers?: PublicOfferSummary[];
};

function Chips({ value, icon: Icon }: { value: string; icon: React.ElementType }) {
  const items = value.split(',').map((v) => v.trim()).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <Badge key={item} variant="secondary" className="gap-1 font-normal">
          <Icon className="h-3 w-3" />
          {item}
        </Badge>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

export default function AdvisorProfileDetail({
  profileId,
  languages,
  country,
  cities,
  onOffers,
}: {
  profileId: string;
  languages?: string;
  country?: string;
  cities?: string;
  onOffers?: (offers: PublicOfferSummary[]) => void;
}) {
  const [data, setData] = React.useState<PublicProfilePayload | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    fetch(`${publicApiBaseUrl()}/public/advisors/${profileId}/`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        if (!live) return;
        setData(payload);
        onOffers?.(payload.public_offers ?? []);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  if (failed) {
    return (
      <p className="py-4 text-sm text-muted-foreground" data-testid="advisor-detail-failed">
        Could not load the rest of this profile.
      </p>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const profile = data.profile;
  const facts = [
    profile.years_experience ? { icon: Award, text: `${profile.years_experience} years in the field` } : null,
    country ? { icon: MapPin, text: cities ? `${cities}, ${country}` : country } : null,
    languages ? { icon: Languages, text: languages } : null,
  ].filter(Boolean) as Array<{ icon: React.ElementType; text: string }>;

  return (
    <div className="space-y-6 text-sm" data-testid={`advisor-detail-${profileId}`}>
      {facts.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-muted-foreground">
          {facts.map(({ icon: Icon, text }) => (
            <span key={text} className="inline-flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" />
              {text}
            </span>
          ))}
        </div>
      )}

      {profile.bio && (
        <Section title="About">
          <div
            className="space-y-2 text-foreground/90 [&_a]:underline [&_a]:underline-offset-4 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: markdown.render(profile.bio) }}
          />
        </Section>
      )}

      {profile.specializations && (
        <Section title="What they do">
          <Chips value={profile.specializations} icon={Tag} />
        </Section>
      )}

      {profile.industries && (
        <Section title="Industries">
          <Chips value={profile.industries} icon={Tag} />
        </Section>
      )}

      {profile.certifications?.length > 0 && (
        <Section title="Certifications">
          <div className="flex flex-wrap gap-1.5">
            {profile.certifications.map((c) => (
              <span key={c} className="rounded-full border px-2.5 py-0.5 text-xs">{c}</span>
            ))}
          </div>
        </Section>
      )}

      {data.blog_posts.length > 0 && (
        <Section title="Recent articles">
          <ul className="space-y-1.5">
            {data.blog_posts.slice(0, 4).map((post) => (
              <li key={post.id}>
                <Link
                  to={`/public/advisor/${profileId}/blog/${post.slug}`}
                  target="_blank"
                  className="inline-flex items-start gap-1.5 text-foreground underline-offset-4 hover:underline"
                >
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  {post.title}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Link
        to={`/public/advisor/${profileId}`}
        target="_blank"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-4 hover:underline"
        data-testid={`advisor-public-page-${profileId}`}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Open their public page
      </Link>
    </div>
  );
}
